import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Package, ChevronDown, ChevronRight } from "lucide-react";
import {
  EquipmentCoverageRowView,
  AssetCondition,
  getEquipmentCoverage,
} from "../../../../api/General/assets";
import Button from "@/components/ui/Button";

interface EquipmentCoverageTabProps {
  // Switch to the Assets tab filtered to the needed category + Available so HR can issue a matching asset.
  onIssueItem: (neededItem: string) => void;
}

const conditionLabelKey: Record<AssetCondition, string> = {
  [AssetCondition.Excellent]: "assets.conditionExcellent",
  [AssetCondition.Good]: "assets.conditionGood",
  [AssetCondition.Fair]: "assets.conditionFair",
  [AssetCondition.Poor]: "assets.conditionPoor",
};

const EquipmentCoverageTab: React.FC<EquipmentCoverageTabProps> = ({ onIssueItem }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<EquipmentCoverageRowView[]>([]);
  const [underCovered, setUnderCovered] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getEquipmentCoverage();
      if (result.errors.length === 0) {
        setRows(result.payload.rows);
        setUnderCovered(result.payload.underCoveredCount);
        setTotal(result.payload.totalWithRequirements);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const coverageColor = (pct: number) =>
    pct >= 100 ? "text-success" : pct >= 60 ? "text-amber-600" : "text-destructive";
  const conditionColor = (pct?: number) =>
    pct == null ? "text-muted-foreground" : pct >= 80 ? "text-success" : pct >= 50 ? "text-amber-600" : "text-destructive";

  if (loading) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label={t("common.loading")}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="py-12 text-center" data-testid="coverage-empty">
        <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">{t("assets.coverage.empty")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm">
        <span className="text-muted-foreground">{t("assets.coverage.withRequirements", { count: total })}</span>
        {underCovered > 0 && (
          <span
            className="inline-flex items-center rounded-full border border-amber-400 text-amber-700 bg-amber-50 px-2.5 py-0.5 text-xs font-medium"
            title={t("assets.coverage.badgeTooltip")}
            data-testid="coverage-under-count"
          >
            {t("assets.coverage.underCovered", { count: underCovered })}
          </span>
        )}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="text-start font-medium px-4 py-2.5 w-8"></th>
              <th className="text-start font-medium px-4 py-2.5">{t("assets.coverage.colEmployee")}</th>
              <th className="text-start font-medium px-4 py-2.5">{t("assets.coverage.colDepartment")}</th>
              <th className="text-start font-medium px-4 py-2.5">{t("assets.coverage.colRole")}</th>
              <th className="text-end font-medium px-4 py-2.5">{t("assets.coverage.colCoverage")}</th>
              <th className="text-end font-medium px-4 py-2.5">{t("assets.coverage.colCondition")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const open = !!expanded[r.employeeId];
              return (
                <React.Fragment key={r.employeeId}>
                  <tr
                    className="border-t border-border hover:bg-muted/30 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                    onClick={() => toggle(r.employeeId)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(r.employeeId); } }}
                    tabIndex={0}
                    aria-expanded={open}
                    data-testid={`coverage-row-${r.employeeId}`}
                  >
                    <td className="px-4 py-2.5">
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-foreground">{r.employeeName || "-"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.department || "-"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.roleName || "-"}</td>
                    <td className={`px-4 py-2.5 text-end font-semibold ${coverageColor(r.coveragePercent)}`}>
                      {r.coveragePercent}%
                    </td>
                    <td className={`px-4 py-2.5 text-end font-semibold ${conditionColor(r.conditionPercent)}`} data-testid={`coverage-condition-${r.employeeId}`}>
                      {r.conditionPercent == null ? "-" : `${r.conditionPercent}%`}
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-muted/20 border-t border-border">
                      <td></td>
                      <td colSpan={5} className="px-4 py-3">
                        <ul className="space-y-3">
                          {r.items.map((item) => (
                            <li
                              key={item.neededItem}
                              className="space-y-1.5"
                              data-testid={`coverage-item-${r.employeeId}-${item.neededItem}`}
                            >
                              <div className="flex items-center justify-between gap-4">
                                <span className="font-medium text-foreground">{item.neededItem}</span>
                                {item.isCovered ? (
                                  <span className={`text-xs font-medium ${conditionColor(item.conditionPercent)}`}>
                                    {t("assets.coverage.conditionLabel", { pct: item.conditionPercent ?? 0 })}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-3">
                                    <span className="text-destructive text-xs font-medium">{t("assets.coverage.notIssued")}</span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => { e.stopPropagation(); onIssueItem(item.neededItem); }}
                                      data-testid={`coverage-issue-${r.employeeId}-${item.neededItem}`}
                                    >
                                      {t("assets.coverage.issueItem")}
                                    </Button>
                                  </span>
                                )}
                              </div>
                              {item.matchedAssets.length > 0 && (
                                <ul className="ms-4 space-y-1">
                                  {item.matchedAssets.map((a, i) => (
                                    <li key={`${a.assetName}-${i}`} className="text-xs text-muted-foreground flex items-center gap-2">
                                      <span className="text-foreground">{a.assetName}</span>
                                      <span>· {t(conditionLabelKey[a.condition])} · {a.weight}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EquipmentCoverageTab;
